import React from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import { withFormik } from 'formik';

import { trackEvent } from 'src/lib/analytics';
import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import pd from 'src/lib/api/pagerduty';
import { textifyError } from 'src/lib/error';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';
import { IncidentsSelect } from 'src/views/components/select';

const analyticsEventName = 'sidebar_link_incident';

function closeModal(e) {
  e.preventDefault();
  jira.closeCurrentModal();
}

const LinkIncidentForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleSubmit,
  isSubmitting,
  status,
}) => (
  <form onSubmit={handleSubmit} style={{ display: 'flex', flexFlow: 'column nowrap', minHeight: '300px' }}>
    <SectionMessage title="Incidents ordered by urgency">
      <p>Select an incident from the last 100 incidents ordered by urgency.</p>
    </SectionMessage>

    <br/>

    <Field name="incident" label="PagerDuty Incident" isRequired errorMessage={touched.incident && errors.incident}>
      <IncidentsSelect
        value={values.incident}
        onChange={handleChange}
        name="incident"
        className="incidents-dropdown"
      />
    </Field>

    <br/>

    <div style={{ flexGrow: 1 }}/>

    {!isSubmitting && status && status.error && <div style={{ marginTop: 24 }}>
      <ErrorMessage error={status.error}/>
    </div>}

    {!isSubmitting && status && status.warning && <div style={{ marginTop: 24 }}>
      <ErrorMessage error={status.warning} appearance="warning"/>
    </div>}

    <div style={{ paddingTop: 24, paddingBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
      <ButtonGroup>
        <Button onClick={closeModal} isDisabled={Boolean(status && status.warning)}>Cancel</Button>
        <LoadingButton type="submit" appearance="primary" isLoading={isSubmitting} isDisabled={Boolean(status && status.warning)}>Link incident</LoadingButton>
        {status && Boolean(status.warning) && <Button appearance="primary" onClick={closeModal}>Close</Button>}
      </ButtonGroup>
    </div>
  </form>
);

const putIncidentIdIntoIssueProps = (issue, incidentId) => Promise.all([
  jira.setIssueProperty(issue.key, 'pagerdutyIncident', { connected: true }), // pagerdutyIncident is used by v3 integration backend
  jira.setIssueProperty(issue.key, 'pagerduty', { incidentId }) //pagerduty.incidentId is used by this code, linkedIncident service
])
  .then(_ => ({ linked: true }))
  .catch(e => ({ error: ['Can not Update Jira Issue Properties.', e] }));

const isWebhookV3 = (webhook, issue) => {
  const oiName = String(webhook.outbound_integration && webhook.outbound_integration.summary);
  const { project, issueType } = (webhook.config && webhook.config.jira) || {};
  const projectIdFromWebhook = project && project.id;
  const issueTypeFromWebhook = issueType && issueType.id;
  const projectFromIssue = issue.fields && issue.fields.project && issue.fields.project.id;
  const issueTypeFromIssue = issue.fields && issue.fields.issuetype && issue.fields.issuetype.id;

  return oiName.toLowerCase().includes('jira')
    && projectFromIssue === projectIdFromWebhook && projectFromIssue
    && issueTypeFromIssue === issueTypeFromWebhook && issueTypeFromIssue;
};

const isWebhookV4 = (webhook, accountMappingId) => {
  const serviceId = webhook && webhook.webhook_object && webhook.webhook_object.id;
  return serviceId && accountMappingId && webhook.name === `JIRA_integration_${accountMappingId}_${serviceId}`;
};

const webhookConfigMatchesIssue = (webhook, issue, accountMappingId) => isWebhookV4(webhook, accountMappingId) || isWebhookV3(webhook, issue);

const putIssueKeyIntoIncidentExternalReference = async (issue, incidentId, accountMappingId) => {
  try {
    const incident = await pd
      .getIncident(incidentId, { include: ['webhooks', 'services', 'external_references'] })
      .catch(e => Promise.reject([`Failed to load incident ${incidentId}`, e]));

    const webhooks = (incident && incident.service && incident.service.webhooks) || [];
    const [webhook] = webhooks.filter(w => webhookConfigMatchesIssue(w, issue, accountMappingId));
    if (webhook) {
      const sameIer = ((incident && incident.external_references) || []).find(e => e && e.webhook && e.webhook.id === webhook.id);
      if (sameIer) {
        return { error: `Incident #${incident.incident_number} is already linked to issue ${sameIer.external_id}.
        Automatic update rules will continue working with ${sameIer.external_id}, the current issue (${issue.key}) is linked for sidebar access only.` };
      }
      const { browseBaseUrl } = await jira.getContext();
      await pd.putIncidentExternalReference(
        incidentId,
        webhook.id,
        issue.key,
        `${browseBaseUrl}/browse/${issue.key}`,
        'Jira'
      )
        .catch(e => Promise.reject([`Failed to update PagerDurty incident External Reference`, e]));

      return { linked: true };
    }

    return { linked: false };
  } catch (error) {
    return { error };
  }
};

const putIncidentIssueMappingIntoService = async (issue, incidentId) => {
  try {
    const accMapping = await integration.getAccountMapping();
    if (accMapping) {
      await integration.createIncidentIssueMapping(incidentId, issue.key);
      return { linked: true };
    } else {
      return { linked: false };
    }
  } catch (error) {
    return { error };
  }
};

const LinkIncidentFormWithFormik = withFormik({
  mapPropsToValues: () => ({
    incident: null,
  }),

  validate: values => {
    const errors = {};

    if (!values.incident) {
      errors.incident = 'Incident is required';
    }

    return errors;
  },

  handleSubmit: async (
    { incident: { id: incidentId } },
    {
      props: {
        issue,
        accountMapping,
      },
      setSubmitting,
      setStatus
    }
  ) => {
    trackEvent(analyticsEventName, { label: 'link_incident' });
    const results = await Promise.all([
      putIncidentIdIntoIssueProps(issue, incidentId),
      putIssueKeyIntoIncidentExternalReference(issue, incidentId, accountMapping && accountMapping.id),
      putIncidentIssueMappingIntoService(issue, incidentId)
    ]);

    const errors = results.map(r => r && r.error).filter(e => e);

    if (errors.length === 0) {
      jira.closeCurrentModal();
      return;
    }

    const details = errors.map(e => textifyError(e)).join('. ');

    if (results.some(r => r && r.linked)) {
      setStatus({
        warning: [
          'Incident is linked, but some errors occurred. Further synchronisation might suffer.',
          details
        ]
      }
      );
    } else {
      setStatus({ error: ['Failed to link incident', details] });
    }

    setSubmitting(false);
  },
})(LinkIncidentForm);

export default LinkIncidentFormWithFormik;
